import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { getPostAuthPath, storePendingInvitation, getPendingInvitation } from '../utils/userCompany';
import LoadingSpinner from '../components/LoadingSpinner';
import './Register.css';

const RegisterContent = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(false);
  const { register, loginWithGoogle, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!invitationToken) {
      return;
    }

    storePendingInvitation(invitationToken);

    setLoadingInvitation(true);
    axios.get(`/api/companies/invitations/token/${invitationToken}`)
      .then((response) => {
        const info = response.data.data;
        setInvitationInfo(info);
        setFormData((prev) => ({
          ...prev,
          email: info.email
        }));
      })
      .catch((err) => {
        setError(formatError(err) || t('auth:invalidInvitation'));
      })
      .finally(() => {
        setLoadingInvitation(false);
      });
  }, [invitationToken, t]);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setError('');
      const pendingToken = invitationToken || getPendingInvitation();
      const result = await loginWithGoogle(credentialResponse.credential, pendingToken);
      if (result.success) {
        navigate(getPostAuthPath(result.user));
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Google registration error:', err);
      setError(t('auth:googleRegisterFailed'));
    }
  };

  const handleGoogleError = () => {
    setError(t('auth:googleRegisterFailed'));
  };

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const result = await register({
      ...formData,
      invitationToken: invitationToken || undefined
    });
    if (result.success) {
      navigate(getPostAuthPath(result.user));
    } else {
      setError(result.error);
    }
  };

  if (loadingInvitation) {
    return (
      <div className="register-container">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="register-container">
      <div className="register-card">
        <h2>
          {invitationInfo
            ? t('auth:joinCompany', { companyName: invitationInfo.companyName })
            : t('auth:registerTitle')}
        </h2>
        {invitationInfo && (
          <p className="register-invitation-note">
            {t('auth:invitationNote', {
              inviterName: invitationInfo.inviterName || t('auth:yourTeam')
            })}
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('auth:firstName')}</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('auth:lastName')}</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('auth:email')}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              readOnly={!!invitationInfo}
            />
          </div>
          <div className="form-group">
            <label>{t('auth:phoneOptional')}</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>{t('auth:password')}</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
            />
          </div>
          {error && <div className="error">{error}</div>}
          <p className="register-legal-notice">
            {t('auth:legalNoticePrefix')}{' '}
            <Link to="/terms">{t('common:termsOfService')}</Link>{' '}
            {t('auth:legalNoticeAnd')}{' '}
            <Link to="/privacy">{t('common:privacyPolicy')}</Link>.
          </p>
          <button type="submit" className="btn btn-primary">
            {invitationInfo ? t('auth:createAccountJoin') : t('auth:register')}
          </button>
        </form>
        {googleClientId && (
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '10px', color: '#666' }}>{t('auth:or')}</div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="signup_with"
                useOneTap={false}
              />
            </div>
          </div>
        )}
        <p className="login-link">
          {t('auth:hasAccount')}{' '}
          <a href={invitationToken ? `/login?invitation=${invitationToken}` : '/login'}>
            {t('auth:loginHere')}
          </a>
        </p>
      </div>
    </div>
  );
};

const Register = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (googleClientId) {
    return (
      <GoogleOAuthProvider clientId={googleClientId}>
        <RegisterContent />
      </GoogleOAuthProvider>
    );
  }

  return <RegisterContent />;
};

export default Register;
